import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2, Mic, MicOff } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

type Question = {
  question: string;
  image: string;
  options: string[];
  answer: string;
};

const ALL_QUESTIONS: Question[] = [
  // أركان الإسلام
  { question: "كم عدد أركان الإسلام؟", image: "🕌", options: ["خمسة", "أربعة", "ستة", "ثلاثة"], answer: "خمسة" },
  { question: "ما أول ركن من أركان الإسلام؟", image: "☝️", options: ["الشهادتان", "الصلاة", "الزكاة", "الصيام"], answer: "الشهادتان" },
  { question: "كم صلاة نصلّي في اليوم؟", image: "🤲", options: ["خمس صلوات", "ثلاث صلوات", "أربع صلوات", "ست صلوات"], answer: "خمس صلوات" },
  { question: "في أي شهر نصوم؟", image: "🌙", options: ["رمضان", "شعبان", "محرم", "رجب"], answer: "رمضان" },
  { question: "ما هي الزكاة؟", image: "💰", options: ["إعطاء المال للفقراء", "الصلاة بالليل", "قراءة القرآن", "صيام النهار"], answer: "إعطاء المال للفقراء" },
  { question: "أين يذهب المسلمون للحج؟", image: "🕋", options: ["مكة المكرمة", "المدينة المنورة", "القدس", "دمشق"], answer: "مكة المكرمة" },
  // أركان الإيمان
  { question: "كم عدد أركان الإيمان؟", image: "💫", options: ["ستة", "خمسة", "أربعة", "سبعة"], answer: "ستة" },
  { question: "من هم الملائكة؟", image: "✨", options: ["مخلوقات من نور", "مخلوقات من نار", "مخلوقات من طين", "مخلوقات من ماء"], answer: "مخلوقات من نور" },
  { question: "ما هو كتاب المسلمين؟", image: "📖", options: ["القرآن الكريم", "التوراة", "الإنجيل", "الزبور"], answer: "القرآن الكريم" },
  // الأنبياء والرسل
  { question: "من هو خاتم الأنبياء والرسل؟", image: "🌟", options: ["محمد ﷺ", "عيسى عليه السلام", "موسى عليه السلام", "إبراهيم عليه السلام"], answer: "محمد ﷺ" },
  { question: "من النبي الذي بنى الكعبة؟", image: "🕋", options: ["إبراهيم عليه السلام", "محمد ﷺ", "نوح عليه السلام", "آدم عليه السلام"], answer: "إبراهيم عليه السلام" },
  { question: "من النبي الذي بنى السفينة؟", image: "🚢", options: ["نوح عليه السلام", "موسى عليه السلام", "يونس عليه السلام", "صالح عليه السلام"], answer: "نوح عليه السلام" },
  { question: "من أول نبي خلقه الله؟", image: "🌍", options: ["آدم عليه السلام", "نوح عليه السلام", "إبراهيم عليه السلام", "محمد ﷺ"], answer: "آدم عليه السلام" },
  { question: "من النبي الذي ابتلعه الحوت؟", image: "🐋", options: ["يونس عليه السلام", "نوح عليه السلام", "موسى عليه السلام", "داود عليه السلام"], answer: "يونس عليه السلام" },
  { question: "من النبي الذي كلّمه الله في الجبل؟", image: "⛰️", options: ["موسى عليه السلام", "عيسى عليه السلام", "إبراهيم عليه السلام", "محمد ﷺ"], answer: "موسى عليه السلام" },
  // القرآن والسور
  { question: "ما أول سورة في القرآن الكريم؟", image: "📗", options: ["الفاتحة", "البقرة", "الناس", "الإخلاص"], answer: "الفاتحة" },
  { question: "ما آخر سورة في القرآن الكريم؟", image: "📕", options: ["الناس", "الفلق", "الإخلاص", "الكوثر"], answer: "الناس" },
  { question: "ماذا نقول قبل قراءة القرآن؟", image: "📖", options: ["أعوذ بالله من الشيطان", "الحمد لله", "سبحان الله", "لا إله إلا الله"], answer: "أعوذ بالله من الشيطان" },
  // الأذكار والآداب
  { question: "ماذا نقول قبل الأكل؟", image: "🍽️", options: ["بسم الله", "الحمد لله", "سبحان الله", "الله أكبر"], answer: "بسم الله" },
  { question: "ماذا نقول بعد الأكل؟", image: "😊", options: ["الحمد لله", "بسم الله", "ما شاء الله", "إن شاء الله"], answer: "الحمد لله" },
  { question: "ماذا نقول عند العطس؟", image: "🤧", options: ["الحمد لله", "يرحمك الله", "بسم الله", "سبحان الله"], answer: "الحمد لله" },
  { question: "ماذا نقول عند دخول المسجد؟", image: "🕌", options: ["اللهم افتح لي أبواب رحمتك", "الحمد لله", "بسم الله", "سبحان الله"], answer: "اللهم افتح لي أبواب رحمتك" },
  { question: "ماذا نقول قبل النوم؟", image: "😴", options: ["باسمك اللهم أموت وأحيا", "الحمد لله", "سبحان الله", "بسم الله"], answer: "باسمك اللهم أموت وأحيا" },
  { question: "ماذا نقول عند الاستيقاظ؟", image: "🌅", options: ["الحمد لله الذي أحيانا", "بسم الله", "سبحان الله", "الله أكبر"], answer: "الحمد لله الذي أحيانا" },
  // القيم الإسلامية
  { question: "ما أهم صفة يحبها الله؟", image: "❤️", options: ["الصدق", "الكذب", "الغش", "الكبر"], answer: "الصدق" },
  { question: "كيف نعامل الجار؟", image: "🏘️", options: ["بالإحسان والمحبة", "بالتجاهل", "بالصوت العالي", "بعدم الاهتمام"], answer: "بالإحسان والمحبة" },
  { question: "ماذا يجب على المسلم تجاه والديه؟", image: "👨‍👩‍👧‍👦", options: ["البر والإحسان", "عدم الطاعة", "التجاهل", "الصوت العالي"], answer: "البر والإحسان" },
  { question: "ما حكم مساعدة المحتاجين؟", image: "🤝", options: ["أمر عظيم يحبه الله", "غير مهم", "ممنوع", "مكروه"], answer: "أمر عظيم يحبه الله" },
  // معلومات عامة
  { question: "ما اسم قبلة المسلمين؟", image: "🧭", options: ["الكعبة المشرفة", "المسجد النبوي", "المسجد الأقصى", "مسجد قباء"], answer: "الكعبة المشرفة" },
  { question: "في أي يوم صلاة الجمعة؟", image: "📅", options: ["الجمعة", "السبت", "الأحد", "الاثنين"], answer: "الجمعة" },
];

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function IslamicGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds, timeout } = getDifficultyConfig(difficulty);
  const [questions] = useState(() => shuffleArray([...ALL_QUESTIONS]).slice(0, totalRounds));
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const data = questions[round] || questions[0];

  const { speak, isListening, startListening, stopListening, encourageCorrect, encourageWrong } = useSpeech({
    onVoiceResult: (transcript) => {
      if (selected !== null || round >= totalRounds) return;
      const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF\s]/g, "").trim();
      const spoken = normalizeAr(transcript);
      const match = data.options.find(opt => {
        const n = normalizeAr(opt);
        return spoken.includes(n) || n.includes(spoken);
      });
      if (match) handleSelect(match);
    },
  });

  const handleSelect = (opt: string) => {
    if (selected !== null) return;
    setSelected(opt);
    if (opt === data.answer) {
      setScore((s) => s + 10);
      setStreak((s) => s + 1);
      encourageCorrect();
    } else {
      setStreak(0);
      encourageWrong();
    }
    setTimeout(() => {
      setSelected(null);
      setRound((r) => r + 1);
    }, timeout);
  };

  const reset = () => {
    setScore(0);
    setStreak(0);
    setRound(0);
    setSelected(null);
    setCompleted(false);
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }} className="text-6xl">
              🕌
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">ما شاء الله! بارك الله فيك 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
            <Badge className="bg-gradient-to-r from-orange-400 to-red-500 text-white border-0 text-sm px-3 py-1">🔥 سلسلة {streak}</Badge>
          </motion.div>
        )}
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-emerald-600" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={round}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Card className="shadow-elevated overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-teal-500 to-emerald-700 p-6 text-center text-white relative">
                <div className="absolute top-3 left-3 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak(data.question + ". الخيارات: " + data.options.join("، "))}>
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <Button variant={isListening ? "destructive" : "ghost"} size="icon" className={`h-8 w-8 rounded-full ${isListening ? "animate-pulse" : "text-white/80 hover:text-white hover:bg-white/20"}`} onClick={() => isListening ? stopListening() : startListening()} disabled={!!selected}>
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                </div>
                <motion.span className="text-7xl block mb-4" animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>{data.image}</motion.span>
                <p className="text-lg font-medium opacity-95">{data.question}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3">
        {data.options.map((opt, i) => {
          const isSelected = selected === opt;
          const showResult = selected !== null;
          const isAnswer = opt === data.answer;
          let bgClass = "bg-card hover:bg-accent border-2 border-border hover:border-primary/50";
          if (showResult && isAnswer) bgClass = "bg-emerald-100 border-2 border-emerald-500 dark:bg-emerald-900/30";
          else if (isSelected && !isAnswer) bgClass = "bg-red-100 border-2 border-red-400 dark:bg-red-900/30";

          return (
            <motion.button
              key={opt}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={!selected ? { scale: 1.05 } : {}}
              whileTap={!selected ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(opt)}
              disabled={!!selected}
              className={`rounded-xl p-4 text-center transition-all ${bgClass}`}
            >
              <span className="text-base font-bold text-foreground">{opt}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
